/**
 * EditableText — texto que se lee como documento y se edita al hacer clic.
 *
 * Reemplaza a los <textarea>/<input> siempre visibles de la nota clínica: en
 * reposo es texto plano (sin bordes ni cajas); al pasar el mouse aparece una
 * pista discreta y al hacer clic (o Enter con el foco puesto) el bloque se
 * vuelve editable en el sitio. Al salir (blur) confirma el cambio; Escape
 * cancela y restaura el valor anterior.
 *
 * El valor se sincroniza al DOM de forma imperativa — React no administra los
 * hijos de un nodo contentEditable (de ahí `suppressContentEditableWarning`).
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ClipboardEvent } from 'react';
import styles from './EditableText.module.css';

/**
 * `plaintext-only` evita que el navegador inserte HTML al pegar y que Enter
 * genere <div>/<br> anidados. Chrome/Safari lo soportan desde hace años y
 * Firefox desde la v136; donde no exista se cae a `true` + saneo del pegado.
 */
const PLAINTEXT_ONLY: boolean = (() => {
  if (typeof document === 'undefined') return false;
  try {
    const probe = document.createElement('div');
    probe.contentEditable = 'plaintext-only';
    return probe.contentEditable === 'plaintext-only';
  } catch {
    return false;
  }
})();

/** Coloca el cursor donde se hizo clic; si no hay punto, al final del texto. */
function colocarCursor(el: HTMLElement, e?: { clientX: number; clientY: number }) {
  const sel = window.getSelection();
  if (!sel) return;
  let range: Range | null = null;
  if (e && Number.isFinite(e.clientX)) {
    const d = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };
    if (typeof d.caretRangeFromPoint === 'function') {
      range = d.caretRangeFromPoint(e.clientX, e.clientY);
    } else if (typeof d.caretPositionFromPoint === 'function') {
      const pos = d.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }
  }
  if (!range || !el.contains(range.startContainer)) {
    range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

export interface EditableTextProps {
  value: string;
  /** Se llama SOLO cuando el texto realmente cambió al terminar la edición. */
  onCommit: (next: string) => void;
  placeholder?: string;
  /** Pista que aparece al pasar el mouse. */
  hint?: string;
  /** `block` = párrafo de la nota · `inline` = nombre/detalle en una línea. */
  variant?: 'block' | 'inline';
  /** Solo lectura: se ve igual pero no admite edición. */
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function EditableText({
  value,
  onCommit,
  placeholder,
  hint = 'clic para editar',
  variant = 'block',
  disabled = false,
  className = '',
  ariaLabel,
}: EditableTextProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);

  // Fuente de verdad SÍNCRONA de si se está editando: `editing` (estado) solo
  // gobierna estilos y ARIA, y llega un render tarde. No se re-asigna en cada
  // render para no pisar el valor que `empezar` acaba de fijar.
  const editingRef = useRef(false);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // El valor externo (IA, asistente, snapshot) solo entra al DOM cuando el
  // médico NO está escribiendo — así un ciclo de IA nunca le mueve el cursor.
  useEffect(() => {
    const el = ref.current;
    if (!el || editingRef.current) return;
    if (el.textContent !== value) el.textContent = value;
  }, [value]);

  /**
   * Entra en edición de forma SÍNCRONA. Si se esperara al re-render de React
   * para poner `contentEditable` y el foco, las pulsaciones ocurridas entre el
   * clic y ese foco se perderían, y el cursor saltaría al final en vez de
   * quedar donde el médico hizo clic.
   */
  const empezar = useCallback(
    (e?: { clientX: number; clientY: number }) => {
      if (disabled || editingRef.current) return;
      const el = ref.current;
      if (el) {
        el.contentEditable = PLAINTEXT_ONLY ? 'plaintext-only' : 'true';
        editingRef.current = true;
        el.focus({ preventScroll: true });
        colocarCursor(el, e);
      }
      setEditing(true);
    },
    [disabled],
  );

  const terminar = useCallback(() => {
    // Se lee del ref, no del estado: si el foco se pierde en el mismo tick en
    // que empezó la edición, el estado `editing` todavía es false y un
    // `onBlur` condicionado a él dejaría el cambio sin confirmar.
    if (!editingRef.current) return;
    const el = ref.current;
    editingRef.current = false;
    setEditing(false);
    if (!el) return;
    el.contentEditable = 'false';
    // El navegador inserta &nbsp; al escribir espacios seguidos.
    const texto = (el.textContent ?? '').replace(/\u00a0/g, ' ');
    if (texto !== valueRef.current) onCommit(texto);
  }, [onCommit]);

  const alPresionarTecla = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!editingRef.current) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          empezar();
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        const el = ref.current;
        if (el) {
          el.textContent = valueRef.current; // cancelar: restaurar
          el.contentEditable = 'false';
        }
        editingRef.current = false;
        setEditing(false);
        el?.blur();
        return;
      }
      // En una línea (nombre de un medicamento, dosis…) Enter confirma.
      if (variant === 'inline' && e.key === 'Enter') {
        e.preventDefault();
        ref.current?.blur();
      }
    },
    [empezar, variant],
  );

  const alPegar = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    if (PLAINTEXT_ONLY) return; // el navegador ya pega texto plano
    e.preventDefault();
    const texto = e.clipboardData.getData('text/plain');
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    sel.deleteFromDocument();
    sel.getRangeAt(0).insertNode(document.createTextNode(texto));
    sel.collapseToEnd();
  }, []);

  const vacio = !value && !editing;
  const clases = [
    styles.root,
    styles[variant],
    editing ? styles.editando : '',
    vacio ? styles.vacio : '',
    disabled ? styles.bloqueado : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={ref}
      className={clases}
      /* El atributo se gestiona imperativamente en empezar()/terminar(): si
         dependiera del estado, React lo pisaría un render tarde y quedaría
         desincronizado con el DOM que ya modificamos. */
      contentEditable={false}
      suppressContentEditableWarning
      role={editing ? 'textbox' : 'button'}
      aria-label={ariaLabel}
      aria-readonly={!editing}
      aria-multiline={variant === 'block'}
      tabIndex={disabled ? -1 : 0}
      data-placeholder={placeholder}
      data-hint={hint}
      onClick={(e) => empezar({ clientX: e.clientX, clientY: e.clientY })}
      onBlur={terminar}
      onKeyDown={alPresionarTecla}
      onPaste={alPegar}
    />
  );
}
